<template>
  <section aria-labelledby="members-title" class="d-flex flex-column gap-3">
    <div class="d-flex justify-content-between align-items-center">
      <h2 id="members-title" class="h4 mb-0 d-flex align-items-center gap-2">
        <span class="dashboard-area-icon dashboard-area-icon-dark" aria-hidden="true"><font-awesome-icon icon="users" /></span>Mitglieder
      </h2>
      <router-link :to="{ name: 'memberlist' }" class="link-body-emphasis">Mitgliederliste</router-link>
    </div>

    <component :is="canManageMembers ? 'router-link' : 'div'" v-bind="feeBoxLink" class="fee-box text-decoration-none">
      <span class="d-flex flex-column gap-1 flex-grow-1 min-w-0">
        <span class="text-label fee-box-muted">Jahresabrechnung {{ feeYear }}</span>
        <span class="fs-4 fw-bold">Beitragseinzug</span>
        <span class="fee-box-muted">{{ feeDateLine }}</span>
      </span>
      <span class="fee-box-countdown" :aria-label="`Noch ${feeDays} Tage bis zum Beitragseinzug`">
        <span class="fs-3 fw-semibold">{{ feeDays }}</span>
        <span class="small fee-box-muted">Tage</span>
      </span>
    </component>

    <div class="row row-cols-3 g-2 g-lg-3">
      <div class="col">
        <StatCard label="Freigabe offen" :value="canManageMembers ? pendingGroups.length : '–'"
          :attention="pendingGroups.length > 0" />
      </div>
      <div class="col">
        <StatCard label="Mitglieder" :value="memberCount ?? '–'" :to="{ name: 'memberlist' }" />
      </div>
      <div class="col">
        <StatCard :label="`Austritte ${currentYear}`" :value="canManageMembers ? exitsThisYear : '–'" />
      </div>
    </div>

    <template v-if="canManageMembers">
      <p v-if="loadError" class="alert alert-danger mb-0">Die Mitgliederdaten konnten nicht geladen werden.</p>

      <section aria-labelledby="members-todo-title" class="d-flex flex-column gap-2">
        <div class="d-flex justify-content-between align-items-baseline">
          <h3 id="members-todo-title" class="h5 mb-0">Zu erledigen</h3>
          <router-link v-if="pendingGroups.length > 0" :to="{ name: 'membergroups', query: { filter: 'open' } }"
            class="link-body-emphasis">Alle ansehen</router-link>
        </div>
        <p v-if="loading" class="text-body-secondary mb-0">Mitglieder werden geladen …</p>
        <p v-else-if="pendingGroups.length === 0" class="text-body-secondary mb-0">Alles erledigt – keine Mitglieder warten auf Freischaltung.</p>
        <ul v-else class="list-unstyled d-flex flex-column gap-2 mb-0">
          <li v-for="group in visiblePendingGroups" :key="group.id" class="todo-row">
            <span class="todo-dot" aria-hidden="true"></span>
            <span class="d-flex flex-column flex-grow-1 min-w-0">
              <span class="fw-medium text-truncate">{{ memberGroupName(group) }} freischalten</span>
              <span class="small text-body-secondary text-truncate">{{ memberGroupMeta(group) }}</span>
            </span>
            <router-link class="btn btn-dark btn-square" :to="{ name: 'membergroups', query: { group: group.id } }"
              :aria-label="`${memberGroupName(group)} öffnen`">
              <font-awesome-icon icon="chevron-right" />
            </router-link>
          </li>
        </ul>
      </section>

      <section aria-labelledby="members-exits-title" class="d-flex flex-column gap-2">
        <h3 id="members-exits-title" class="h5 mb-0">Zuletzt ausgetreten</h3>
        <p v-if="!loading && exits.length === 0" class="text-body-secondary mb-0">Keine Austritte vorhanden.</p>
        <ul v-else-if="!loading" class="list-group mb-0">
          <li v-for="exit in exits" :key="exit.id" class="list-group-item d-flex align-items-center gap-3">
            <span class="exit-avatar" aria-hidden="true">{{ initials(exit.name) }}</span>
            <span class="d-flex flex-column flex-grow-1 min-w-0">
              <span class="fw-medium text-truncate">{{ exit.name }}</span>
              <span class="small text-body-secondary">{{ exitMeta(exit) }}</span>
            </span>
            <span class="small text-body-secondary text-nowrap">{{ exit.leaveDate ? formatDate(exit.leaveDate) : "Datum unbekannt" }}</span>
          </li>
        </ul>
      </section>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted } from "vue";
import StatCard from "@/components/common/StatCard.vue";
import { formatDate } from "@/composables/dateFormat";
import { feeCollection, memberGroupName, recentExits } from "@/composables/memberStatus";
import { useMemberGroups } from "@/composables/useMemberGroups";

defineOptions({ name: "MembersDashboard" });

const MAX_TODOS = 5;
const MAX_EXITS = 5;

const {
  canManageMembers,
  loading,
  loadError,
  memberCount,
  memberGroups,
  pendingGroups,
  currentYear,
  exitsThisYear,
  loadMemberCount,
  loadMemberGroups,
} = useMemberGroups();

const visiblePendingGroups = computed(() => pendingGroups.value.slice(0, MAX_TODOS));
const exits = computed(() => recentExits(memberGroups.value, MAX_EXITS));

const { year: feeYear, days: feeDays, dateLine: feeDateLine } = feeCollection();
const feeBoxLink = canManageMembers ? { to: { name: "annualbilling" } } : {};

const exitMeta = (exit) => {
  if (exit.count > 1) return `${exit.family ? "Familie" : "Gruppe"} · ${exit.count} Personen`;
  return exit.family ? "Familienmitglied" : "Einzelmitglied";
};

const initials = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

onMounted(() => {
  loadMemberCount();
  if (canManageMembers) {
    loadMemberGroups();
  }
});
</script>
